/**
 * Financial role capability maps.
 * Mirror: functions/financial/roleCapabilities.js
 * Keep both files identical. functions/financial/roleCapabilities.parity.test.js
 * fails if they diverge.
 */

/** Platform product roles for financialDashboard. `admin` is a leftover product-admin grant and is User-level. */
export const FINANCIAL_PLATFORM_ROLES = Object.freeze([
  "cfo",
  "manager",
  "admin",
  "user",
  "viewer",
]);

/** Roles tenant administrators may assign on invite (admin is not a financial tier). */
export const TENANT_ASSIGNABLE_FINANCIAL_ROLES = Object.freeze(
  FINANCIAL_PLATFORM_ROLES.filter((role) => role !== "admin"),
);

/** Unity roles that may lodge claims when financialDashboard is company-allocated. */
export const UNITY_FINANCIAL_CLAIM_ROLES = Object.freeze([
  "facilitator",
  "funder",
  "supervisor",
]);

const FULL_CAPABILITY_TEMPLATE = Object.freeze({
  canAccessFinancialDashboard: true,
  canCreateClaim: true,
  canPerformPendingClaimReview: true,
  canPerformSecondClaimReview: true,
  canApproveRejectClaim: true,
  canAccessClaimsCfoApproval: true,
  canViewAllClaimHistory: true,
  canProcessClaimPayment: true,
  canAccessClaimsAuditLog: true,
  canAccessClaimsFinancialDashboard: true,
  canAccessClaimsSettings: true,
  canAccessRecurringPayments: true,
  canEditRecurringPayments: true,
});

const EXCO_CAPABILITY_TEMPLATE = Object.freeze({
  canAccessFinancialDashboard: true,
  canCreateClaim: true,
  canPerformPendingClaimReview: true,
  canPerformSecondClaimReview: true,
  canApproveRejectClaim: true,
  canAccessClaimsCfoApproval: false,
  canViewAllClaimHistory: true,
  canProcessClaimPayment: false,
  canAccessClaimsAuditLog: true,
  canAccessClaimsFinancialDashboard: true,
  canAccessClaimsSettings: true,
  canAccessRecurringPayments: true,
  canEditRecurringPayments: true,
});

const CLAIM_ONLY_CAPABILITY_TEMPLATE = Object.freeze({
  canAccessFinancialDashboard: true,
  canCreateClaim: true,
  canPerformPendingClaimReview: false,
  canPerformSecondClaimReview: false,
  canApproveRejectClaim: false,
  canAccessClaimsCfoApproval: false,
  canViewAllClaimHistory: false,
  canProcessClaimPayment: false,
  canAccessClaimsAuditLog: false,
  canAccessClaimsFinancialDashboard: false,
  canAccessClaimsSettings: false,
  canAccessRecurringPayments: false,
  canEditRecurringPayments: false,
});

const VIEWER_CAPABILITY_TEMPLATE = Object.freeze({
  canAccessFinancialDashboard: true,
  canCreateClaim: false,
  canPerformPendingClaimReview: false,
  canPerformSecondClaimReview: false,
  canApproveRejectClaim: false,
  canAccessClaimsCfoApproval: false,
  canViewAllClaimHistory: false,
  canProcessClaimPayment: false,
  canAccessClaimsAuditLog: false,
  canAccessClaimsFinancialDashboard: false,
  canAccessClaimsSettings: false,
  canAccessRecurringPayments: false,
  canEditRecurringPayments: false,
});

/** Tenant-scoped capabilities keyed by financialDashboard platform role. */
export const PLATFORM_FINANCIAL_CAPABILITIES = Object.freeze({
  cfo: FULL_CAPABILITY_TEMPLATE,
  manager: EXCO_CAPABILITY_TEMPLATE,
  admin: CLAIM_ONLY_CAPABILITY_TEMPLATE,
  user: CLAIM_ONLY_CAPABILITY_TEMPLATE,
  viewer: VIEWER_CAPABILITY_TEMPLATE,
});

export const UNITY_FINANCIAL_CLAIM_CAPABILITIES = CLAIM_ONLY_CAPABILITY_TEMPLATE;

/**
 * Legacy Hero Users doc sync (productUsers.js bridge only, not used for tenant runtime).
 */
export const FINANCIAL_LEGACY_ROLE_BY_PLATFORM_ROLE = Object.freeze({
  cfo: "CFO",
  manager: "Exco",
  admin: "User",
  user: "User",
  viewer: "Viewer",
});

export const FINANCIAL_LEGACY_ROLE_PRIORITY = Object.freeze([
  "cfo",
  "manager",
  "admin",
  "user",
  "viewer",
]);

/** Legacy Hero dashboard capability groups (Title Case roles). */
export const FINANCIAL_ROLE_GROUPS = Object.freeze({
  canAccessFinancialDashboard: Object.freeze([
    "Exco",
    "CFO",
    "Admin",
    "Management",
    "CEO",
    "Facilitator",
    "User",
    "Viewer",
  ]),
  canCreateClaim: Object.freeze([
    "Exco",
    "CFO",
    "Admin",
    "Management",
    "CEO",
    "Facilitator",
    "User",
  ]),
  canPerformPendingClaimReview: Object.freeze(["Exco", "CFO"]),
  canPerformSecondClaimReview: Object.freeze(["Exco", "CFO"]),
  canApproveRejectClaim: Object.freeze(["Exco", "CFO"]),
  canAccessClaimsCfoApproval: Object.freeze(["CFO"]),
  canViewAllClaimHistory: Object.freeze(["Exco", "CFO"]),
  canProcessClaimPayment: Object.freeze(["CFO"]),
  canAccessClaimsAuditLog: Object.freeze(["Exco", "CFO"]),
  canAccessClaimsFinancialDashboard: Object.freeze(["Exco", "CFO"]),
  canAccessClaimsSettings: Object.freeze(["Exco", "CFO"]),
  canAccessRecurringPayments: Object.freeze(["Exco", "CFO"]),
  canEditRecurringPayments: Object.freeze(["Exco", "CFO"]),
});

export const AUTH_MATRIX_ELIGIBLE_ROLES = Object.freeze([
  "admin",
  "facilitator",
  "user",
  "viewer",
]);

export const LEGACY_HERO_DASHBOARD_ROLES = Object.freeze([
  "Admin",
  "Management",
  "CEO",
  "CFO",
  "Exco",
  "Facilitator",
  "Viewer",
]);

export const FINANCIAL_CLAIM_CREATE_EXTRA_ROLES = Object.freeze(["financial admin"]);

export const FINANCIAL_CLAIM_CREATE_PLATFORM_ROLES = Object.freeze([
  ...FINANCIAL_PLATFORM_ROLES.filter((role) => role !== "viewer"),
  ...UNITY_FINANCIAL_CLAIM_ROLES,
]);

export const FINANCIAL_CLAIM_CREATE_ROLES = Object.freeze(Array.from(new Set([
  ...FINANCIAL_ROLE_GROUPS.canCreateClaim.map((role) =>
    String(role).trim().toLowerCase(),
  ),
  ...FINANCIAL_CLAIM_CREATE_PLATFORM_ROLES.map((role) =>
    String(role).trim().toLowerCase(),
  ),
  ...FINANCIAL_CLAIM_CREATE_EXTRA_ROLES.map((role) =>
    String(role).trim().toLowerCase(),
  ),
])));

export const FINANCIAL_CLAIM_CREATE_ROLE_SET = new Set(FINANCIAL_CLAIM_CREATE_ROLES);

export const SUPPLIER_ONBOARDING_DENIED_ROLES = Object.freeze(["learner", "student"]);

export const SUPPLIER_ONBOARDING_DENIED_ROLE_SET = new Set(SUPPLIER_ONBOARDING_DENIED_ROLES);

export const SUPPLIER_ONBOARDING_ROLES = Object.freeze(Array.from(new Set([
  ...FINANCIAL_CLAIM_CREATE_ROLES,
  "viewer",
])));

export const SUPPLIER_ONBOARDING_ROLE_SET = new Set(SUPPLIER_ONBOARDING_ROLES);

/** Pending-queue, archive, and profile maintenance, not User/Viewer/Unity claim. */
export const SUPPLIER_FINANCE_ROLES = Object.freeze(["exco", "cfo", "admin", "manager"]);

export const SUPPLIER_FINANCE_ROLE_SET = new Set(SUPPLIER_FINANCE_ROLES);

/** EXCO/CFO (and platform manager) may override a duplicate during onboarding. */
export const SUPPLIER_DUPLICATE_APPROVER_ROLES = Object.freeze(["exco", "cfo", "manager"]);

export const SUPPLIER_DUPLICATE_APPROVER_ROLE_SET = new Set(SUPPLIER_DUPLICATE_APPROVER_ROLES);

export const CAPABILITY_FLAG_KEYS = Object.freeze([
  "canAccessFinancialDashboard",
  "canCreateClaim",
  "canPerformPendingClaimReview",
  "canPerformSecondClaimReview",
  "canApproveRejectClaim",
  "canAccessClaimsCfoApproval",
  "canViewAllClaimHistory",
  "canProcessClaimPayment",
  "canAccessClaimsAuditLog",
  "canAccessClaimsFinancialDashboard",
  "canAccessClaimsSettings",
  "canAccessRecurringPayments",
  "canEditRecurringPayments",
  "canViewRecurringOverdueKpis",
]);

export function normalizePlatformRole(value) {
  return String(value || "").trim().toLowerCase();
}

/** Tenant platform roles are stored as lowercase slugs; legacy Hero uses Title Case labels. */
export function isTenantFinancialPlatformRoleToken(token) {
  const raw = String(token || "").trim();
  const normalized = normalizePlatformRole(raw);
  return FINANCIAL_PLATFORM_ROLES.includes(normalized) && raw === normalized;
}

/** Expand tenant platform roles to legacy Hero role tokens used in UI gates. */
export function expandFinancialRoleTokensForLegacyChecks(roleTokens = []) {
  const expanded = new Set();
  normalizePlatformRoleTokens(roleTokens).forEach((token) => {
    const normalized = normalizePlatformRole(token);
    expanded.add(normalized);
    if (!isTenantFinancialPlatformRoleToken(token)) return;
    const legacyLabel = FINANCIAL_LEGACY_ROLE_BY_PLATFORM_ROLE[normalized];
    if (legacyLabel) {
      expanded.add(normalizePlatformRole(legacyLabel));
    }
  });
  return Array.from(expanded);
}

export function normalizePlatformRoleTokens(value) {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => String(entry || "").split(","))
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

export function hasFinancialClaimRole(roles = [], allowedRoleSet = new Set()) {
  return expandFinancialRoleTokensForLegacyChecks(roles).some((role) =>
    allowedRoleSet.has(role),
  );
}

function rolesIncludeDeniedSupplierOnboardingRole(roles = []) {
  return expandFinancialRoleTokensForLegacyChecks(roles).some((role) =>
    SUPPLIER_ONBOARDING_DENIED_ROLE_SET.has(role),
  );
}

export function canOnboardSupplier(roles = []) {
  if (hasFinancialClaimRole(roles, SUPPLIER_FINANCE_ROLE_SET)) {
    return true;
  }
  if (rolesIncludeDeniedSupplierOnboardingRole(roles)) {
    return false;
  }
  return hasFinancialClaimRole(roles, SUPPLIER_ONBOARDING_ROLE_SET);
}

export function canAccessSupplierFinanceOperations(roles = []) {
  return hasFinancialClaimRole(roles, SUPPLIER_FINANCE_ROLE_SET);
}

export function canApproveSupplierDuplicateOnboarding(roles = []) {
  return hasFinancialClaimRole(roles, SUPPLIER_DUPLICATE_APPROVER_ROLE_SET);
}

/** Manager (EXCO), CFO, and legacy EXCO may refresh risk and approve bank/compliance. */
export function canApproveSupplierBankCompliance(roles = []) {
  return canApproveSupplierDuplicateOnboarding(roles);
}

export function canRequestSupplierCfoReview(roles = []) {
  return canOnboardSupplier(roles) && !canApproveSupplierDuplicateOnboarding(roles);
}

function emptyCapabilities() {
  return Object.fromEntries(CAPABILITY_FLAG_KEYS.map((key) => [key, false]));
}

function finalizeCapabilities(capabilities) {
  return {
    ...capabilities,
    canViewRecurringOverdueKpis: Boolean(capabilities.canAccessClaimsCfoApproval),
    canPerformManualReview:
      Boolean(capabilities.canPerformPendingClaimReview) ||
      Boolean(capabilities.canPerformSecondClaimReview),
  };
}

function mergeCapabilityRecords(records = []) {
  const merged = emptyCapabilities();
  for (const record of records) {
    if (!record) continue;
    CAPABILITY_FLAG_KEYS.forEach((key) => {
      merged[key] = Boolean(merged[key]) || Boolean(record[key]);
    });
  }
  return finalizeCapabilities(merged);
}

export function buildPlatformFinancialCapabilities({
  financialRoles = [],
  unityClaimRoles = [],
} = {}) {
  const capabilityRecords = normalizePlatformRoleTokens(financialRoles)
    .map((role) => PLATFORM_FINANCIAL_CAPABILITIES[normalizePlatformRole(role)])
    .filter(Boolean);

  const unityRecords = normalizePlatformRoleTokens(unityClaimRoles)
    .filter((role) => UNITY_FINANCIAL_CLAIM_ROLES.includes(normalizePlatformRole(role)))
    .map(() => UNITY_FINANCIAL_CLAIM_CAPABILITIES);

  const capabilities = mergeCapabilityRecords([
    ...capabilityRecords,
    ...unityRecords,
  ]);

  return {
    ...capabilities,
    roles: Array.from(new Set([
      ...normalizePlatformRoleTokens(financialRoles).map(normalizePlatformRole),
      ...normalizePlatformRoleTokens(unityClaimRoles)
        .map(normalizePlatformRole)
        .filter((role) => UNITY_FINANCIAL_CLAIM_ROLES.includes(role)),
    ])),
  };
}

export function canPlatformRoleCreateClaim(roleValue) {
  return FINANCIAL_CLAIM_CREATE_PLATFORM_ROLES.includes(
    normalizePlatformRole(roleValue),
  );
}

export function hasViewerFinancialBaseRole(capabilities = {}) {
  const roles = normalizePlatformRoleTokens(capabilities.roles || []).map(normalizePlatformRole);
  if (!roles.includes("viewer")) return false;
  return !roles.some((role) =>
    role === "cfo" || role === "admin" || role === "manager" || role === "user",
  );
}

export function enforceFinancialReadOnlyCapabilities(capabilities = {}) {
  return finalizeCapabilities({
    ...capabilities,
    canCreateClaim: false,
    canPerformPendingClaimReview: false,
    canPerformSecondClaimReview: false,
    canApproveRejectClaim: false,
    canAccessClaimsCfoApproval: false,
    canProcessClaimPayment: false,
    canAccessClaimsSettings: false,
    canEditRecurringPayments: false,
  });
}

export function applyClaimsAccessOverrides(capabilities = {}, override = {}) {
  const normalized = {
    viewAllClaims: Boolean(override?.viewAllClaims),
    accessAuditLogs: Boolean(override?.accessAuditLogs),
    accessFinancialDashboard: Boolean(override?.accessFinancialDashboard),
    financialAdmin: Boolean(override?.financialAdmin),
  };
  const isViewerBase = hasViewerFinancialBaseRole(capabilities);
  const next = { ...capabilities };

  if (normalized.viewAllClaims) {
    next.canViewAllClaimHistory = true;
  }
  if (normalized.accessAuditLogs) {
    next.canAccessClaimsAuditLog = true;
  }
  if (normalized.accessFinancialDashboard) {
    next.canAccessClaimsFinancialDashboard = true;
  }
  if (normalized.financialAdmin) {
    if (isViewerBase) {
      next.canViewAllClaimHistory = true;
      next.canAccessRecurringPayments = true;
    } else {
      next.canAccessFinancialDashboard = true;
      next.canCreateClaim = true;
      next.canPerformPendingClaimReview = true;
      next.canAccessRecurringPayments = true;
    }
  }
  if (normalized.financialAdmin || normalized.accessFinancialDashboard) {
    next.canViewAllClaimHistory = true;
  }

  if (isViewerBase) {
    return enforceFinancialReadOnlyCapabilities(next);
  }

  return finalizeCapabilities(next);
}
